import { timelineSequenceOf } from './homeChatConversation';
import type { HomeChatTurnSubmission } from './useHomeChatTurnSubmission';
import { extractMissionPartnerNativeEntry } from './missionPartnerGuidance';
import type { HomeChatSessionReconciliation } from './useHomeChatSessionReconciliation';
import { useMemo } from 'react';
import { clarifyMissionPartnerGuidance, clarifyPurchaseGuidance, selectPurchaseGuidance } from './api';
import { type PurchaseGuidanceCard, type MissionPartnerGuidanceCard, isCurrentPurchaseGuidanceSelection, normalizeHomeChatCards } from './types';
import { useCallback } from 'react';
import { extractGpcRouterResponse } from './gpcHandoff';
import { extractMrRouterResponse } from './mrHandoff';
import { extractConstructionSowRouterResponse } from './constructionSowHandoff';
import { extractServicesPwsRouterResponse } from './servicesPwsHandoff';
import { HomeChatHttpError, selectSimilarRequirementsType, selectHomeChatIntentChoice, selectWorkflowRecommendation, selectHomeChatActionChoice, selectTrainingAction, cancelSimilarRequirementsCard, openSimilarRequirementsResult, extractHomeChatCardResponse } from './api';
import { normalizeV2Turn } from '../FarChat/api';
import type { RawV2Turn } from '../FarChat/api';
import type { SimilarRequirementsTypeChooserCard, AmbiguousIntentChoiceCard, WorkflowRecommendationChoiceCard, ActionChoiceCard, WorkflowRecommendationType, CreatedHomeChatWorkflow, HomeChatHandoffContext } from './types';
import { isCurrentAmbiguousIntentChoiceSelection, isCurrentWorkflowRecommendationSelection, isCurrentActionChoiceSelection, normalizeCreatedWorkflow } from './types';
import { HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { friendlyTurnErrorMessage } from './HomeChatTurnErrors';
import { ACTION_CHOICE_UNAVAILABLE_COPY, INTENT_CHOICE_UNAVAILABLE_COPY, selectionErrorMessage, intentChoicePathForLabel, actionChoicePendingLabel } from './homeChatChoiceUtils';
import { PENDING_ID, makeRequestId } from './homeChatSessionUtils';
import type { HomeChatPanelState } from './useHomeChatPanelState';
import type { HomeChatHydration } from './useHomeChatHydration';
import type { HomeChatWorkflowHandoffs } from './useHomeChatWorkflowHandoffs';


type Inputs = Pick<HomeChatTurnSubmission, 'sendV2Turn'> & Pick<HomeChatPanelState, 'scrollThreadToLatest' | 'turns' | 'chatCards' | 'inFlight' | 'setFailedPurchaseFollowup' | 'setNewPurchaseGuidanceFocusId' | 'setWorkflowOnlyChoice' | 'setWorkflowOnlyPurchaseGuidance' | 'workflowOnlyChoice' | 'workflowOnlyPurchaseGuidance'> &
Pick<HomeChatSessionReconciliation, 'refreshSessions'> &
Required<Pick<Props, 'workflowOnly' | 'workflowOnlyAudience'>> &
Pick<HomeChatPanelState,
  | 'activeSessionId'
  | 'setBusySimilarRequirementsCardId'
  | 'setChatCards'
  | 'activeSessionIdRef'
  | 'isCompanion'
  | 'busyTrainingActionId'
  | 'setBusyTrainingActionId'
  | 'setNotice'
  | 'workflowRecommendationRequestIdRef'
  | 'cardActionRevisionRef'
  | 'setBusyWorkflowRecommendationCardId'
  | 'setBusyActionChoiceLabel'
  | 'setTurns'
  | 'pendingIntentChoiceRequestIdRef'
  | 'messages'
  | 'setPendingIntentChoiceSelection'
  | 'setPendingRequestIdSynced'
  | 'setInFlight'
  | 'setInFlightUserMsg'
  | 'setInFlightClarification'
  | 'setInFlightTopicFocus'
  | 'setOptimisticGpcOpening'
  | 'setPendingGpcRouter'
  | 'setMessages'
  | 'clearPendingIntentChoiceSelection'
> &
Pick<HomeChatHydration,
  | 'notify'
  | 'applyConstructionSowChatView'
  | 'applyServicesPwsChatView'
  | 'applyMrChatView'
  | 'applyGpcChatView'
  | 'replaceChatCard'
  | 'hydrateDirectSearchThread'
  | 'isStaleResolve'
  | 'clearPendingIfOwned'
> &
Pick<Props, 'onOpenTraining' | 'onStartWorkflowType' | 'onOpenNavigationHandle' | 'onOpenCreatedWorkflow'> &
Pick<HomeChatWorkflowHandoffs,
  | 'confirmWorkflowNavigation'
  | 'getHandoffContext'
>;

export function useHomeChatCardActions({
  sendV2Turn,
  scrollThreadToLatest,
  turns,
  activeSessionId,
  setBusySimilarRequirementsCardId,
  setChatCards,
  notify,
  activeSessionIdRef,
  isCompanion,
  onOpenTraining,
  busyTrainingActionId,
  setBusyTrainingActionId,
  setNotice,
  workflowRecommendationRequestIdRef,
  confirmWorkflowNavigation,
  getHandoffContext,
  cardActionRevisionRef,
  setBusyWorkflowRecommendationCardId,
  onStartWorkflowType,
  setBusyActionChoiceLabel,
  applyConstructionSowChatView,
  applyServicesPwsChatView,
  applyMrChatView,
  applyGpcChatView,
  setTurns,
  replaceChatCard,
  hydrateDirectSearchThread,
  pendingIntentChoiceRequestIdRef,
  messages,
  setPendingIntentChoiceSelection,
  setPendingRequestIdSynced,
  setInFlight,
  setInFlightUserMsg,
  setInFlightClarification,
  setInFlightTopicFocus,
  setOptimisticGpcOpening,
  isStaleResolve,
  setPendingGpcRouter,
  setMessages,
  clearPendingIfOwned,
  clearPendingIntentChoiceSelection,
  onOpenNavigationHandle,
  chatCards,
  inFlight,
  setFailedPurchaseFollowup,
  setNewPurchaseGuidanceFocusId,
  setWorkflowOnlyChoice,
  setWorkflowOnlyPurchaseGuidance,
  workflowOnlyChoice,
  workflowOnlyPurchaseGuidance,
  refreshSessions,
  workflowOnly,
  workflowOnlyAudience,
  onOpenCreatedWorkflow,
}: Inputs) {

  const navigateCreatedWorkflow = useCallback(async (destination: CreatedHomeChatWorkflow, context?: HomeChatHandoffContext) => {
    try {
      if (destination.status !== 'available' || !onOpenCreatedWorkflow) throw new Error('Workflow unavailable');
      await onOpenCreatedWorkflow(destination.flow_id, context);
    } catch {
      if (activeSessionIdRef.current === context?.homeChatSessionId) notify('error', 'This workflow is no longer available.');
    }
  }, [notify, onOpenCreatedWorkflow]);

  const handleOpenCreatedWorkflow = useCallback(async (card: WorkflowRecommendationChoiceCard | ActionChoiceCard) => {
    const sessionId = activeSessionIdRef.current;
    if (!sessionId || sessionId === PENDING_ID || card.created_workflow?.status !== 'available'
      || workflowRecommendationRequestIdRef.current) return;
    const checkpoint = confirmWorkflowNavigation();
    if (checkpoint === false) return;
    const requestId = makeRequestId();
    workflowRecommendationRequestIdRef.current = requestId;
    setBusyWorkflowRecommendationCardId(card.card_id);
    try {
      if (!onOpenCreatedWorkflow) throw new Error('Workflow navigation unavailable');
      await onOpenCreatedWorkflow(card.created_workflow.flow_id, getHandoffContext(undefined, sessionId));
    } catch {
      if (activeSessionIdRef.current === sessionId) notify('error', 'This workflow is no longer available.');
    } finally {
      if (workflowRecommendationRequestIdRef.current === requestId) {
        workflowRecommendationRequestIdRef.current = null;
        setBusyWorkflowRecommendationCardId(null);
      }
    }
  }, [confirmWorkflowNavigation, getHandoffContext, notify, onOpenCreatedWorkflow]);

  // Similar-requirements actions are deliberately card-bound. The panel never
  // re-sends a query/type or constructs a workflow URL; it only relays opaque
  // values emitted by a currently hydrated server card.
  const handleSelectSimilarRequirementsType = useCallback((
    card: SimilarRequirementsTypeChooserCard,
    selectionKey: string
  ) => {
    const sessionId = activeSessionId;
    if (!sessionId || sessionId === PENDING_ID) return;
    setBusySimilarRequirementsCardId(card.card_id);
    void selectSimilarRequirementsType(sessionId, card.card_id, selectionKey, makeRequestId())
      .then((resultCard) => setChatCards((previous) => {
        const selected = previous.map((current) =>
          current.card_id === card.card_id && current.kind === 'similar_requirements_type_chooser'
            ? { ...current, state: 'selected' as const }
            : current
        );
        return selected.some((current) => current.card_id === resultCard.card_id)
          ? selected.map((current) => current.card_id === resultCard.card_id ? resultCard : current)
          : [...selected, resultCard];
      }))
      .catch(() => notify('error', 'That search action is unavailable. Please try again.'))
      .finally(() => setBusySimilarRequirementsCardId(null));
  }, [activeSessionId, notify]);

  const handleCancelSimilarRequirements = useCallback((cardId: string) => {
    const sessionId = activeSessionId;
    if (!sessionId || sessionId === PENDING_ID) return;
    setBusySimilarRequirementsCardId(cardId);
    void cancelSimilarRequirementsCard(sessionId, cardId, makeRequestId())
      .then(() => setChatCards((previous) => previous.map((card) =>
        card.card_id === cardId && card.kind === 'similar_requirements_type_chooser'
          ? { ...card, state: 'cancelled' }
          : card
      )))
      .catch(() => notify('error', 'That search action is unavailable. Please try again.'))
      .finally(() => setBusySimilarRequirementsCardId(null));
  }, [activeSessionId, notify]);

  // This is deliberately separate from ambiguous-intent selection. A
  const handleSelectTrainingAction = useCallback(async (cardId: string) => {
    const sessionId = activeSessionIdRef.current;
    if (!sessionId || sessionId === PENDING_ID || isCompanion || !onOpenTraining) return;
    if (busyTrainingActionId === cardId) return;
    setBusyTrainingActionId(cardId);
    try {
      await selectTrainingAction(sessionId, cardId);
      if (activeSessionIdRef.current === sessionId) onOpenTraining();
    } catch {
      if (activeSessionIdRef.current === sessionId) {
        setNotice({ severity: 'error', message: 'Training is no longer available for this action.' });
        setChatCards((previous) => previous.map((card) =>
          card.kind === 'training_action' && card.card_id === cardId
            ? { ...card, state: 'error', error: 'Training is unavailable' }
            : card
        ));
      }
    } finally {
      setBusyTrainingActionId(null);
    }
  }, [busyTrainingActionId, isCompanion, onOpenTraining]);

  // recommendation authorizes only the App-shell handoff, never flow creation.
  const handleSelectWorkflowRecommendation = useCallback((card: WorkflowRecommendationChoiceCard, selectionKey: string) => {
    const sessionId = activeSessionId;
    if (
      !sessionId || sessionId === PENDING_ID || workflowRecommendationRequestIdRef.current
      || !isCurrentWorkflowRecommendationSelection(card, selectionKey)
    ) return;
    const navigationCheckpoint = confirmWorkflowNavigation();
    if (navigationCheckpoint === false) return;
    const handoffContext = getHandoffContext(undefined, sessionId);
    const actionRevision = cardActionRevisionRef.current;
    const requestId = makeRequestId();
    workflowRecommendationRequestIdRef.current = requestId;
    setBusyWorkflowRecommendationCardId(card.card_id);
    void selectWorkflowRecommendation(sessionId, card.card_id, selectionKey, requestId)
      .then(async ({
        workflow_type,
        entry_mode,
        selected_label,
        workflow_handoff_receipt,
        created_workflow,
      }) => {
        if (
          activeSessionIdRef.current !== sessionId
          || cardActionRevisionRef.current !== actionRevision
        ) return;
        if (confirmWorkflowNavigation(navigationCheckpoint) === false) return;
        setChatCards((previous) => previous.map((current) =>
          current.card_id === card.card_id && current.kind === 'workflow_recommendation_choice'
            ? {
              ...current,
              state: 'selected' as const,
              // The endpoint repeats the card's safe, server-authored label
              // after atomically committing the selection. Do not derive an
              // App workflow type or flow id from this display-only receipt.
              selected_label,
              ...(created_workflow ? { created_workflow } : {}),
              error: null,
            }
            : current
        ));
        if (created_workflow) {
          await navigateCreatedWorkflow(created_workflow, handoffContext);
          return;
        }
        onStartWorkflowType?.(
          workflow_type,
          handoffContext
            ? {
              ...handoffContext,
              workflowHandoffReceipt: workflow_handoff_receipt,
              ...(entry_mode ? { workflowEntryMode: entry_mode } : {}),
            }
            : undefined,
        );
      })
      .catch(() => {
        if (
          activeSessionIdRef.current !== sessionId
          || cardActionRevisionRef.current !== actionRevision
        ) return;
        setChatCards((previous) => previous.map((current) =>
          current.card_id === card.card_id && current.kind === 'workflow_recommendation_choice'
            ? {
              ...current,
              state: 'error' as const,
              error: 'This option is no longer available for this question. Ask this question again, or ask a new question.',
            }
            : current
        ));
      })
      .finally(() => {
        if (workflowRecommendationRequestIdRef.current === requestId) {
          workflowRecommendationRequestIdRef.current = null;
          setBusyWorkflowRecommendationCardId(null);
          setBusyActionChoiceLabel(null);
        }
      });
  }, [activeSessionId, confirmWorkflowNavigation, getHandoffContext, navigateCreatedWorkflow, onStartWorkflowType]);

  const handleSelectActionChoice = useCallback((card: ActionChoiceCard, selectionKey: string) => {
    const sessionId = activeSessionId;
    if (!sessionId || sessionId === PENDING_ID || workflowRecommendationRequestIdRef.current || !isCurrentActionChoiceSelection(card, selectionKey)) return;
    const selectedOption = card.options.find(
      (option) => option.selection_key === selectionKey,
    );
    // The server discloses only whether an opaque choice leaves this workflow;
    // it never exposes the private dispatch action or destination type.
    const navigationCheckpoint = selectedOption?.requires_workflow_navigation === true
      ? confirmWorkflowNavigation()
      : undefined;
    if (navigationCheckpoint === false) return;
    const handoffContext = getHandoffContext(undefined, sessionId);
    const actionRevision = cardActionRevisionRef.current;
    const requestId = makeRequestId();
    workflowRecommendationRequestIdRef.current = requestId;
    setBusyWorkflowRecommendationCardId(card.card_id);
    setBusyActionChoiceLabel(actionChoicePendingLabel(selectedOption?.label || ''));
    void selectHomeChatActionChoice(sessionId, card.card_id, selectionKey, requestId)
      .then(async (raw) => {
        if (
          activeSessionIdRef.current !== sessionId
          || cardActionRevisionRef.current !== actionRevision
        ) return;
        if (!raw || typeof raw !== 'object') throw new Error('choice unavailable');
        const body = raw as Record<string, unknown>;
        const selectedLabel = typeof body.selected_label === 'string' ? body.selected_label : undefined;
        if (body.created_workflow !== undefined) {
          const createdWorkflow = normalizeCreatedWorkflow(body.created_workflow);
          if (!createdWorkflow) throw new Error('choice unavailable');
          if (confirmWorkflowNavigation(navigationCheckpoint) === false) return;
          setChatCards((previous) => previous.map((current) => current.card_id === card.card_id && current.kind === 'action_choice'
            ? { ...current, state: 'selected' as const, selected_label: selectedLabel, created_workflow: createdWorkflow, error: null } : current));
          await navigateCreatedWorkflow(createdWorkflow, handoffContext);
          return;
        }
        const workflowHandoffReceipt = typeof body.workflow_handoff_receipt === 'string'
          ? body.workflow_handoff_receipt
          : undefined;
        // Action-choice cards normally keep their server dispatch private, but
        // the standalone estimator is a bounded, server-issued entry variation.
        // Preserve it only when the response has the exact trusted shape; a
        // displayed estimator label without that mode must never fall back to
        // an ordinary construction launch.
        const workflowEntryMode = body.entry_mode === undefined
          ? undefined
          : body.entry_mode === 'standalone_price_estimator'
            ? body.entry_mode
            : null;
        const isPriceEstimatorHandoff = (
          body.workflow_type === 'one_time_construction'
          && selectedLabel === 'Construction Price Estimator'
        );
        if (
          workflowEntryMode === null
          || (workflowEntryMode === 'standalone_price_estimator'
            && body.workflow_type !== 'one_time_construction')
          || (isPriceEstimatorHandoff
            && workflowEntryMode !== 'standalone_price_estimator')
        ) {
          throw new Error('choice unavailable');
        }
        if (
          typeof body.workflow_type === 'string'
          && selectedLabel
          && workflowHandoffReceipt
        ) {
          // Older persisted cards do not carry the navigation hint, so their
          // first confirmation happens here, once the response proves this is
          // a workflow handoff. Newer cards recheck the preflight checkpoint
          // to catch edits made while the request was pending.
          if (confirmWorkflowNavigation(navigationCheckpoint) === false) return;
          setChatCards((previous) => previous.map((current) => current.card_id === card.card_id && current.kind === 'action_choice'
          ? { ...current, state: 'selected' as const, selected_label: selectedLabel, error: null } : current));
          onStartWorkflowType?.(
            body.workflow_type as WorkflowRecommendationType,
            handoffContext
              ? {
                ...handoffContext,
                workflowHandoffReceipt,
                ...(workflowEntryMode ? { workflowEntryMode } : {}),
              }
              : undefined,
          );
          return;
        }
        const constructionSowResponse = extractConstructionSowRouterResponse(raw);
        if (constructionSowResponse) {
          setChatCards((previous) => previous.map((current) => current.card_id === card.card_id && current.kind === 'action_choice'
          ? { ...current, state: 'selected' as const, selected_label: selectedLabel || 'Generate new Construction SOW', error: null } : current));
          applyConstructionSowChatView(constructionSowResponse);
          return;
        }
        const servicesPwsResponse = extractServicesPwsRouterResponse(raw);
        if (servicesPwsResponse) {
          setChatCards((previous) => previous.map((current) => current.card_id === card.card_id && current.kind === 'action_choice'
          ? { ...current, state: 'selected' as const, selected_label: selectedLabel || 'Generate Services SOW/PWS', error: null } : current));
          applyServicesPwsChatView(servicesPwsResponse);
          return;
        }
        const mrResponse = extractMrRouterResponse(raw);
        if (mrResponse) {
          setChatCards((previous) => previous.map((current) => current.card_id === card.card_id && current.kind === 'action_choice'
          ? { ...current, state: 'selected' as const, selected_label: selectedLabel || 'Start Market Research chat', error: null } : current));
          applyMrChatView(mrResponse);
          return;
        }
        const gpcResponse = extractGpcRouterResponse(raw);
        if (gpcResponse?.kind === 'chat') {
          setChatCards((previous) => previous.map((current) => current.card_id === card.card_id && current.kind === 'action_choice'
          ? { ...current, state: 'selected' as const, selected_label: selectedLabel || 'Start GPC purchase chat', error: null } : current));
          applyGpcChatView(gpcResponse.view);
          return;
        }
        if (body.status === 'qa' && body.turn && typeof body.turn === 'object') {
          const turn = normalizeV2Turn(body.turn as RawV2Turn);
          setChatCards((previous) => previous.map((current) => current.card_id === card.card_id && current.kind === 'action_choice'
          ? { ...current, state: 'selected' as const, selected_label: selectedLabel || 'Get procurement guidance', error: null } : current));
          setTurns((previous) => previous.some((existing) => existing.turn_id === turn.turn_id) ? previous : [...previous, turn]);
          return;
        }
        const cardResponse = extractHomeChatCardResponse(raw);
        if (cardResponse) {
          if (cardResponse.chat_card.kind === 'purchase_guidance') {
            setNewPurchaseGuidanceFocusId(cardResponse.chat_card.card_id);
          }
          if (cardResponse.chat_card.kind === 'similar_requirements_results') {
            // The similar-results card is a new conversation item, while the
            // selected action card is its completed routing predecessor.
            // Mark that predecessor before the authoritative thread refresh
            // so its buttons cannot briefly reappear between the response and
            // hydration.
            setChatCards((previous) => {
              const selected = previous.map((current) => current.card_id === card.card_id && current.kind === 'action_choice'
                ? {
                  ...current,
                  state: 'selected' as const,
                  selected_label: selectedLabel || 'Find similar past requirements',
                  error: null,
                }
                : current);
              return selected.some((current) => current.card_id === cardResponse.chat_card.card_id)
                ? selected.map((current) => current.card_id === cardResponse.chat_card.card_id ? cardResponse.chat_card : current)
                : [...selected, cardResponse.chat_card];
            });
          } else {
            // Multi-step action choices replace the same durable card in
            // place. Do not render an intermediate selected/loading receipt:
            // it causes a visible flash and can briefly duplicate the chooser
            // during a rapid server response.
            replaceChatCard(cardResponse.chat_card);
          }
          await hydrateDirectSearchThread(
            cardResponse.session_id || sessionId,
            () => (
              activeSessionIdRef.current === sessionId
              && cardActionRevisionRef.current === actionRevision
            ),
          );
          return;
        }
        throw new Error('choice unavailable');
      })
      .catch((error: unknown) => {
        if (
          activeSessionIdRef.current !== sessionId
          || cardActionRevisionRef.current !== actionRevision
        ) return;
        setChatCards((previous) => previous.map((current) => current.card_id === card.card_id && current.kind === 'action_choice'
          ? {
            ...current,
            state: 'error' as const,
            error: error instanceof HomeChatHttpError && error.status >= 500 && error.status < 600
              ? error.message
              : ACTION_CHOICE_UNAVAILABLE_COPY,
          } : current));
      })
      .finally(() => {
        if (workflowRecommendationRequestIdRef.current === requestId) {
          workflowRecommendationRequestIdRef.current = null;
          setBusyWorkflowRecommendationCardId(null);
          setBusyActionChoiceLabel(null);
        }
      });
  }, [
    activeSessionId,
    confirmWorkflowNavigation,
    applyConstructionSowChatView,
    applyGpcChatView,
    applyMrChatView,
    getHandoffContext,
    hydrateDirectSearchThread,
    onStartWorkflowType,
    navigateCreatedWorkflow,
  ]);

  const handleSelectPurchaseGuidance = useCallback((card: PurchaseGuidanceCard, selectionKey: string) => {
    const sessionId = activeSessionId;
    if (!sessionId || sessionId === PENDING_ID || workflowRecommendationRequestIdRef.current
      || !isCurrentPurchaseGuidanceSelection(card, selectionKey)) return;
    const actionRevision = cardActionRevisionRef.current;
    const requestId = makeRequestId();
    workflowRecommendationRequestIdRef.current = requestId;
    setBusyWorkflowRecommendationCardId(card.card_id);
    void selectPurchaseGuidance(sessionId, card.card_id, selectionKey, requestId)
      .then(async (raw) => {
        if (activeSessionIdRef.current !== sessionId || cardActionRevisionRef.current !== actionRevision) return;
        const mr = extractMrRouterResponse(raw);
        if (mr) {
          if (workflowOnly) setWorkflowOnlyPurchaseGuidance(null);
          applyMrChatView(mr);
          await refreshSessions();
          return;
        }
        const gpc = extractGpcRouterResponse(raw);
        if (gpc?.kind === 'chat') {
          if (workflowOnly) setWorkflowOnlyPurchaseGuidance(null);
          applyGpcChatView(gpc.view);
          await refreshSessions();
          return;
        }
        const cardResponse = extractHomeChatCardResponse(raw);
        if (cardResponse?.chat_card.kind === 'purchase_guidance') {
          if (workflowOnly) setWorkflowOnlyPurchaseGuidance(cardResponse.chat_card);
          else replaceChatCard(cardResponse.chat_card);
          await hydrateDirectSearchThread(sessionId, () => activeSessionIdRef.current === sessionId
            && cardActionRevisionRef.current === actionRevision);
          await refreshSessions();
          return;
        }
        const body = raw && typeof raw === 'object' ? raw as Record<string, unknown> : null;
        if (body?.status === 'workflow_entry'
          && (body.workflow_type === 'one_time_construction'
            || body.workflow_type === 'services'
            || body.workflow_type === 'complex_services')
          && typeof body.workflow_handoff_receipt === 'string'
          && body.workflow_handoff_receipt.trim() && !workflowOnly) {
          onStartWorkflowType?.(body.workflow_type, {
            homeChatSessionId: sessionId,
            workflowHandoffReceipt: body.workflow_handoff_receipt,
          });
          await hydrateDirectSearchThread(sessionId, () => activeSessionIdRef.current === sessionId
            && cardActionRevisionRef.current === actionRevision);
          return;
        }
        throw new Error('This purchase route is no longer available.');
      })
      .catch((error: unknown) => {
        if (activeSessionIdRef.current !== sessionId || cardActionRevisionRef.current !== actionRevision) return;
        const unavailable: PurchaseGuidanceCard = {
          ...card, state: 'error', options: [],
          support_text: 'This choice needs a current policy and access review. Contact the local contracting office.',
        };
        if (workflowOnly) setWorkflowOnlyPurchaseGuidance(unavailable);
        else replaceChatCard(unavailable);
        notify('error', error instanceof Error ? error.message : 'This purchase route is no longer available.');
        void hydrateDirectSearchThread(sessionId, () => activeSessionIdRef.current === sessionId
          && cardActionRevisionRef.current === actionRevision);
      })
      .finally(() => {
        if (workflowRecommendationRequestIdRef.current === requestId) {
          workflowRecommendationRequestIdRef.current = null;
          setBusyWorkflowRecommendationCardId(null);
        }
      });
  }, [activeSessionId, applyGpcChatView, applyMrChatView, hydrateDirectSearchThread, notify, onStartWorkflowType, refreshSessions, replaceChatCard, workflowOnly]);

  const handleSelectMissionPartnerGuidance = useCallback((card: MissionPartnerGuidanceCard, selectionKey: string) => {
    const sessionId = activeSessionId;
    if (!sessionId || sessionId === PENDING_ID || isCompanion || !onStartWorkflowType
      || workflowRecommendationRequestIdRef.current || !['ready', 'selected'].includes(card.state)
      || !card.options.some(option => option.selection_key === selectionKey)) return;
    const checkpoint = confirmWorkflowNavigation();
    if (checkpoint === false) return;
    const context = getHandoffContext(undefined, sessionId);
    const revision = cardActionRevisionRef.current;
    const requestId = makeRequestId();
    workflowRecommendationRequestIdRef.current = requestId;
    setBusyWorkflowRecommendationCardId(card.card_id);
    void selectHomeChatActionChoice(sessionId, card.card_id, selectionKey, requestId).then(async raw => {
      if (activeSessionIdRef.current !== sessionId || cardActionRevisionRef.current !== revision) return;
      const entry = extractMissionPartnerNativeEntry(raw);
      const expected = card.options.find(option => option.selection_key === selectionKey);
      if (!entry || entry.workflowType !== expected?.workflow_type || entry.entryMode !== expected?.entry_mode) throw new Error('This workflow is no longer available.');
      if (confirmWorkflowNavigation(checkpoint) === false) return;
      onStartWorkflowType(entry.workflowType, { ...context, homeChatSessionId: sessionId,
        workflowHandoffReceipt: entry.receipt, ...(entry.entryMode ? { workflowEntryMode: entry.entryMode } : {}) });
      await hydrateDirectSearchThread(sessionId, () => activeSessionIdRef.current === sessionId && cardActionRevisionRef.current === revision);
    }).catch(error => {
      if (activeSessionIdRef.current === sessionId && cardActionRevisionRef.current === revision) {
        notify('error', error instanceof Error ? error.message : 'This workflow is unavailable.');
        void hydrateDirectSearchThread(sessionId, () => activeSessionIdRef.current === sessionId && cardActionRevisionRef.current === revision);
      }
    }).finally(() => {
      if (workflowRecommendationRequestIdRef.current === requestId) { workflowRecommendationRequestIdRef.current = null; setBusyWorkflowRecommendationCardId(null); }
    });
  }, [activeSessionId, activeSessionIdRef, cardActionRevisionRef, isCompanion, onStartWorkflowType, confirmWorkflowNavigation, getHandoffContext, hydrateDirectSearchThread, notify, setBusyWorkflowRecommendationCardId, workflowRecommendationRequestIdRef]);

  const currentPurchaseClarification = useMemo(() => {
    const cards = workflowOnly ? (workflowOnlyPurchaseGuidance ? [workflowOnlyPurchaseGuidance] : []) : chatCards;
    const latest = [...cards].sort((a, b) => b.timeline_sequence - a.timeline_sequence)[0];
    const latestAnswerSequence = Math.max(-1, ...turns.map(turn => timelineSequenceOf(turn) ?? -1));
    if (latest && latestAnswerSequence > latest.timeline_sequence) return null;
    if (latest?.kind === 'mission_partner_guidance') return latest;
    return latest?.kind === 'purchase_guidance' && latest.variant === 'clarification' && latest.state === 'support'
      ? latest : null;
  }, [chatCards, turns, workflowOnly, workflowOnlyPurchaseGuidance]);

  // RA follow-ups can leave purchase guidance and become an ordinary product
  // choice. Keep the latest server-issued card as the free-text continuation
  // target; older cards remain history, not instructions for the next turn.
  const currentRaFollowupCard = useMemo(() => {
    if (!workflowOnly || !['ra', 'ce'].includes(workflowOnlyAudience || '')) return null;
    const cards = chatCards.filter((item): item is PurchaseGuidanceCard | MissionPartnerGuidanceCard | ActionChoiceCard =>
      item.kind === 'purchase_guidance' || item.kind === 'mission_partner_guidance' || item.kind === 'action_choice');
    const latest = [...cards].sort((a, b) => b.timeline_sequence - a.timeline_sequence)[0];
    if (latest) return latest;
    const projectedChoice = normalizeHomeChatCards([workflowOnlyChoice])[0];
    return projectedChoice?.kind === 'action_choice' ? projectedChoice : workflowOnlyPurchaseGuidance;
  }, [chatCards, workflowOnly, workflowOnlyAudience, workflowOnlyChoice, workflowOnlyPurchaseGuidance]);

  const handlePurchaseClarification = useCallback(async (text: string, retryRequestId?: string) => {
    const message = text.trim();
    const card = currentRaFollowupCard || currentPurchaseClarification;
    const sessionId = activeSessionId;
    if (!message || !card || !sessionId || sessionId === PENDING_ID || inFlight
      || workflowRecommendationRequestIdRef.current) return;
    const requestId = retryRequestId || makeRequestId();
    const actionRevision = cardActionRevisionRef.current;
    workflowRecommendationRequestIdRef.current = requestId;
    setFailedPurchaseFollowup(null);
    // A typed follow-up is a new chat turn, not a selection on the previous
    // guidance card. Its ordinary thread indicator is the sole loading UI.
    setInFlight(true);
    setInFlightUserMsg(message);
    scrollThreadToLatest();
    try {
      const raw = await (card.kind === 'mission_partner_guidance' ? clarifyMissionPartnerGuidance : clarifyPurchaseGuidance)(sessionId, card.card_id, message, requestId);
      if (activeSessionIdRef.current !== sessionId || cardActionRevisionRef.current !== actionRevision) return;
      const response = raw && typeof raw === 'object' ? raw as Record<string, unknown> : null;
      if (response?.status === 'continue_full_chat' && response.session_id === sessionId && !workflowOnly) {
        setPendingRequestIdSynced(requestId);
        await sendV2Turn(sessionId, message, requestId);
        await refreshSessions();
        return;
      }
      const mr = extractMrRouterResponse(raw);
      if (mr) { applyMrChatView(mr); await refreshSessions(); return; }
      const construction = extractConstructionSowRouterResponse(raw);
      if (construction) { applyConstructionSowChatView(construction); await refreshSessions(); return; }
      const services = extractServicesPwsRouterResponse(raw);
      if (services) { applyServicesPwsChatView(services); await refreshSessions(); return; }
      const gpc = extractGpcRouterResponse(raw);
      if (gpc?.kind === 'chat') { applyGpcChatView(gpc.view); await refreshSessions(); return; }
      const cardResponse = extractHomeChatCardResponse(raw);
      if (response?.session_id !== sessionId || !cardResponse
        || (workflowOnly && cardResponse.chat_card.kind !== 'purchase_guidance'
          && cardResponse.chat_card.kind !== 'mission_partner_guidance' && cardResponse.chat_card.kind !== 'action_choice')) {
        throw new Error('The clarification response is unavailable.');
      }
      if (workflowOnly) {
        // The POST has persisted the user turn and card atomically. Keep their
        // server-owned ordering visible while the thread refresh is in flight.
        const userSequence = cardResponse.chat_card.timeline_sequence - 1;
        setMessages((previous) => previous.some((item) => item.timeline_sequence === userSequence)
          ? previous : [...previous, { role: 'user', content: message, timeline_sequence: userSequence }]);
        setWorkflowOnlyPurchaseGuidance(cardResponse.chat_card.kind === 'purchase_guidance' || cardResponse.chat_card.kind === 'mission_partner_guidance' ? cardResponse.chat_card : null);
        setWorkflowOnlyChoice(cardResponse.chat_card.kind === 'action_choice' ? cardResponse.chat_card : null);
        setChatCards((previous) => [...previous.filter((item) => item.card_id !== cardResponse.chat_card.card_id), cardResponse.chat_card]);
      }
      else setChatCards((previous) => [...previous, cardResponse.chat_card]);
      await hydrateDirectSearchThread(sessionId, () => activeSessionIdRef.current === sessionId
        && cardActionRevisionRef.current === actionRevision);
      await refreshSessions();
      if (activeSessionIdRef.current === sessionId && cardActionRevisionRef.current === actionRevision) scrollThreadToLatest();
    } catch (error) {
      if (activeSessionIdRef.current === sessionId && cardActionRevisionRef.current === actionRevision) {
        setFailedPurchaseFollowup({ message, requestId });
        notify('error', error instanceof Error ? error.message : 'Unable to continue this purchase clarification.');
      }
    } finally {
      if (workflowRecommendationRequestIdRef.current === requestId) {
        workflowRecommendationRequestIdRef.current = null;
        setInFlight(false);
        setInFlightUserMsg(null);
      }
    }
  }, [activeSessionId, currentPurchaseClarification, currentRaFollowupCard, hydrateDirectSearchThread, inFlight, notify, refreshSessions, workflowOnly, sendV2Turn, scrollThreadToLatest, setPendingRequestIdSynced, applyMrChatView, applyConstructionSowChatView, applyServicesPwsChatView, applyGpcChatView]);

  const handleSelectAmbiguousIntentChoice = useCallback((
    card: AmbiguousIntentChoiceCard,
    selectionKey: string,
  ) => {
    const sessionId = activeSessionId;
    if (!sessionId || sessionId === PENDING_ID || pendingIntentChoiceRequestIdRef.current) return;
    // The UI cannot manufacture an action. It can only relay a still-current
    // server-issued selection key from a normalized card. The server remains
    // authoritative for eligibility and rechecks it at selection time.
    if (!isCurrentAmbiguousIntentChoiceSelection(card, selectionKey)) {
      setChatCards((previous) => previous.map((current) =>
        current.card_id === card.card_id && current.kind === 'ambiguous_intent_choice'
          ? {
            ...current,
            state: 'error' as const,
            error: INTENT_CHOICE_UNAVAILABLE_COPY,
          }
          : current
      ));
      return;
    }
    const selectedOption = card.options.find((option) => option.selection_key === selectionKey);
    const path = selectedOption ? intentChoicePathForLabel(selectedOption.label) : null;
    if (!path) {
      setChatCards((previous) => previous.map((current) =>
        current.card_id === card.card_id && current.kind === 'ambiguous_intent_choice'
          ? { ...current, state: 'error' as const, error: INTENT_CHOICE_UNAVAILABLE_COPY }
          : current
      ));
      return;
    }
    const requestId = makeRequestId();
    const openingMessage = [...messages].reverse().find((message) => message.role === 'user')?.content || card.prompt;
    // Set this before dispatching. React's state update is asynchronous, so
    // the ref closes the tiny double-click window before the selector unmounts.
    pendingIntentChoiceRequestIdRef.current = requestId;
    setPendingIntentChoiceSelection({ cardId: card.card_id, requestId, path, openingMessage });
    setPendingRequestIdSynced(requestId);
    setInFlight(true);
    // The selector's original user message is already persisted and rendered
    // in this thread. Q&A and Similar Requirements therefore enter the same
    // checklist loading surface as a direct v2 turn, but must not add a
    // second optimistic user bubble. GPC has its own controller-equivalent
    // handoff view, so it intentionally does not use this shared bubble.
    if (path !== 'gpc') {
      setInFlightUserMsg(null);
      setInFlightClarification(null);
      setInFlightTopicFocus(null);
    }
    if (path === 'gpc') setOptimisticGpcOpening(openingMessage);
    void selectHomeChatIntentChoice(sessionId, card.card_id, selectionKey, requestId)
      .then(async (raw) => {
        if (isStaleResolve(requestId)) return;
        const mrResponse = extractMrRouterResponse(raw);
        if (mrResponse) {
          setChatCards((previous) => previous.map((current) =>
            current.card_id === card.card_id && current.kind === 'ambiguous_intent_choice'
              ? { ...current, state: 'selected' as const }
              : current
          ));
          applyMrChatView(mrResponse);
          return;
        }
        const gpcResponse = extractGpcRouterResponse(raw);
        if (gpcResponse?.kind === 'chat') {
          setChatCards((previous) => previous.map((current) =>
            current.card_id === card.card_id && current.kind === 'ambiguous_intent_choice'
              ? { ...current, state: 'selected' as const }
              : current
          ));
          applyGpcChatView(gpcResponse.view);
          return;
        }
        if (gpcResponse?.kind === 'start_confirmation' || gpcResponse?.kind === 'ambiguous_choice' || gpcResponse?.kind === 'resume_selection') {
          setOptimisticGpcOpening(null);
          setPendingGpcRouter(gpcResponse);
          setChatCards((previous) => previous.map((current) =>
            current.card_id === card.card_id && current.kind === 'ambiguous_intent_choice'
              ? { ...current, state: 'selected' as const }
              : current
          ));
          return;
        }
        const cardResponse = extractHomeChatCardResponse(raw);
        if (!cardResponse) {
          const safeError = selectionErrorMessage(raw);
          if (safeError) {
            setChatCards((previous) => previous.map((current) =>
              current.card_id === card.card_id && current.kind === 'ambiguous_intent_choice'
                ? { ...current, state: 'error' as const, error: safeError }
                : current
            ));
            return;
          }
          const qaResponse = raw as { status?: unknown; turn?: unknown };
          if (qaResponse.status !== 'qa' || !qaResponse.turn || typeof qaResponse.turn !== 'object') {
            throw new Error('choice unavailable');
          }
          const turn = normalizeV2Turn(qaResponse.turn as RawV2Turn);
          setChatCards((previous) => previous.map((current) =>
            current.card_id === card.card_id && current.kind === 'ambiguous_intent_choice'
              ? { ...current, state: 'selected' as const }
              : current
          ));
          // The backend consumes the most recent matching persisted user
          // message when it converts this explicit Q&A choice into a v2
          // Turn. Mirror that replacement locally so the opening question
          // remains visible exactly once, now through `turn.user_msg`.
          // Similar Requirements deliberately does not take this path: its
          // result card retains the original durable user message.
          setMessages((previous) => {
            for (let index = previous.length - 1; index >= 0; index -= 1) {
              const message = previous[index];
              if (message.role === 'user' && message.content === openingMessage) {
                return [...previous.slice(0, index), ...previous.slice(index + 1)];
              }
            }
            return previous;
          });
          setTurns((previous) => previous.some((existing) => existing.turn_id === turn.turn_id)
            ? previous
            : [...previous, turn]);
          return;
        }
        setChatCards((previous) => {
          const selected = previous.map((current) =>
            current.card_id === card.card_id && current.kind === 'ambiguous_intent_choice'
              ? { ...current, state: 'selected' as const }
              : current
          );
          return selected.some((current) => current.card_id === cardResponse.chat_card.card_id)
            ? selected.map((current) => current.card_id === cardResponse.chat_card.card_id ? cardResponse.chat_card : current)
            : [...selected, cardResponse.chat_card];
        });
        await hydrateDirectSearchThread(
          cardResponse.session_id || sessionId,
          () => !isStaleResolve(requestId),
        );
      })
      .catch((error: unknown) => {
        if (isStaleResolve(requestId)) return;
        setOptimisticGpcOpening(null);
        // A stale/revoked server-issued key is intentionally indistinguishable
        // from any other unavailable action. Do not preserve its controls for
        // a retry or expose entitlement/policy details in the browser.
        setChatCards((previous) => previous.map((current) =>
          current.card_id === card.card_id && current.kind === 'ambiguous_intent_choice'
            ? {
              ...current,
              state: 'error' as const,
              // Only a backend-masked 500 carries approved friendly turn
              // copy. Revoked/foreign/expired selection keys intentionally
              // stay indistinguishable and use the stable unavailable copy.
              error: error instanceof HomeChatHttpError && error.status === 500
                ? friendlyTurnErrorMessage(error)
                : INTENT_CHOICE_UNAVAILABLE_COPY,
            }
            : current
        ));
      })
      .finally(() => {
        if (isStaleResolve(requestId)) return;
        setInFlight(false);
        clearPendingIfOwned(requestId);
        clearPendingIntentChoiceSelection(requestId);
        setInFlightUserMsg(null);
        setInFlightClarification(null);
        setInFlightTopicFocus(null);
      });
  }, [
    activeSessionId,
    applyGpcChatView,
    applyMrChatView,
    clearPendingIfOwned,
    clearPendingIntentChoiceSelection,
    hydrateDirectSearchThread,
    isStaleResolve,
    messages,
    setPendingRequestIdSynced,
  ]);

  const handleOpenSimilarRequirementsResult = useCallback(async (
    cardId: string,
    openHandle: string
  ) => {
    const sessionId = activeSessionId;
    if (!sessionId || sessionId === PENDING_ID || !onOpenNavigationHandle) return;
    // The next request exchanges a one-time open handle. Ask before spending
    // it so cancellation leaves the server-authored result fully reusable.
    if (confirmWorkflowNavigation() === false) return;
    const handoffContext = getHandoffContext(undefined, sessionId);
    const actionRevision = cardActionRevisionRef.current;
    setBusySimilarRequirementsCardId(cardId);
    try {
      const instruction = await openSimilarRequirementsResult(sessionId, openHandle);
      if (
        activeSessionIdRef.current !== sessionId
        || cardActionRevisionRef.current !== actionRevision
      ) return;
      await onOpenNavigationHandle(
        instruction.navigation_handle,
        handoffContext,
      );
    } catch {
      if (
        activeSessionIdRef.current !== sessionId
        || cardActionRevisionRef.current !== actionRevision
      ) return;
      notify('error', 'This workflow is no longer available.');
    } finally {
      if (
        activeSessionIdRef.current === sessionId
        && cardActionRevisionRef.current === actionRevision
      ) {
        setBusySimilarRequirementsCardId(null);
      }
    }
  }, [
    activeSessionId,
    confirmWorkflowNavigation,
    getHandoffContext,
    notify,
    onOpenNavigationHandle,
  ]);

  return {
    handleSelectPurchaseGuidance,
    handleSelectMissionPartnerGuidance,
    currentPurchaseClarification,
    currentRaFollowupCard,
    handlePurchaseClarification,

    handleOpenCreatedWorkflow,
    handleSelectSimilarRequirementsType,
    handleCancelSimilarRequirements,
    handleSelectTrainingAction,
    handleSelectWorkflowRecommendation,
    handleSelectActionChoice,
    handleSelectAmbiguousIntentChoice,
    handleOpenSimilarRequirementsResult,
  };
}

export type HomeChatCardActions = ReturnType<typeof useHomeChatCardActions>;
